import { z } from "zod";

import type { ProgressionRule, Sequence } from "../programProgression.js";
import { MAX_ROUND_SETS as MAX_SETS } from "./program.js";
import { AmrapStop, REPS_RANGE_MAX } from "./programV3.js";

// #351: the wire form of a progression rule (programProgression.ts), so a
// program model can store one in DDB. The checks at the bottom tie each
// schema to its type: a field added to one and not the other fails the build.

const MAX_ROUNDS = 12;

const Num = z.number().finite();
const Rounds = z.number().int().min(1).max(MAX_ROUNDS);

const Constant = z.object({ kind: z.literal("constant"), value: Num });
const Linear = z.object({ kind: z.literal("linear"), start: Num, step: Num, cap: Num.optional() });
const Ramp = z.object({ kind: z.literal("ramp"), from: Num, to: Num, rounds: Rounds });
const Cycle = z.object({ kind: z.literal("cycle"), values: z.array(Num).min(1).max(MAX_ROUNDS) });
const Blocks = z.object({
  kind: z.literal("blocks"),
  // The nested sequence is typed by Sequence itself: TS cannot infer a
  // recursive schema, and the top level is still checked field by field.
  get blocks(): z.ZodArray<z.ZodObject<{ rounds: typeof Rounds; seq: z.ZodType<Sequence> }>> {
    return z.array(z.object({ rounds: Rounds, seq: SequenceSchema })).min(1).max(MAX_ROUNDS);
  },
});

export const SequenceSchema = z.discriminatedUnion("kind", [Constant, Linear, Ramp, Cycle, Blocks]);

export const ThresholdSchema = z.object({
  repsMin: z.number().int().min(0),
  repsMax: z.number().int().min(0),
  repsStep: z.number().int().min(1),
  setsMax: z.number().int().min(1).max(MAX_SETS).optional(),
  loadStart: Num,
  loadStep: Num,
});

export const TestEffectSchema = z.object({
  amrapLast: z.boolean().optional(),
  amrapStop: AmrapStop.optional(),
  delta: Num.optional(),
  on: z.enum(["all", "last", "top"]).optional(),
});

export const DeloadEffectSchema = z.object({
  factor: z.number().positive().max(1).optional(),
  delta: Num.optional(),
  setsDelta: z.number().int().max(0).optional(),
});

const RepsEnd = z.number().int().min(0).max(REPS_RANGE_MAX);

export const ProgressionRuleSchema = z.object({
  axis: z.enum(["none", "absolute", "percent-of-max", "history", "rpe", "rir"]),
  work: z.literal("countdown").optional(),
  sets: z.number().int().min(1).max(MAX_SETS),
  reps: SequenceSchema,
  value: SequenceSchema.nullable(),
  perSet: z
    .object({
      reps: z.array(Num).max(MAX_SETS).optional(),
      value: z.array(Num).max(MAX_SETS).optional(),
    })
    .optional(),
  range: z.object({ min: RepsEnd, max: RepsEnd }).optional(),
  threshold: ThresholdSchema.optional(),
  test: TestEffectSchema.optional(),
  deload: DeloadEffectSchema.optional(),
});

/** True only when A and B are the same type. */
type Same<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false;

true satisfies Same<z.infer<typeof SequenceSchema>, Sequence>;
true satisfies Same<z.infer<typeof ProgressionRuleSchema>, ProgressionRule>;
